import { useTranslation } from "react-i18next";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import Card from "react-bootstrap/esm/Card";
import Collapse from "react-bootstrap/esm/Collapse";

interface CSVImportSectionProps {
    showImportSection: boolean;
    setShowImportSection: (show: boolean) => void;
    importing: boolean;
    importResults: string[];
    onCSVImport: (e: React.FormEvent<HTMLFormElement>) => void;
}

export default function CSVImportSection({
    showImportSection,
    setShowImportSection,
    importing,
    importResults,
    onCSVImport,
}: CSVImportSectionProps) {
    const { t } = useTranslation();

    return (
        <>
            {/* Toggle Button in Header */}
            <Button
                variant={showImportSection ? "secondary" : "outline-primary"}
                onClick={() => setShowImportSection(!showImportSection)}
                className="d-flex align-items-center gap-2"
            >
                📥 {t("admin.csvImport")}
            </Button>

            {/* Collapsible CSV Import Section */}
            <Collapse in={showImportSection}>
                <div>
                    <Card className="mb-4 shadow-sm border-0" style={{ backgroundColor: "var(--gl-surface-raised)" }}>
                        <Card.Body>
                            <h5 className="mb-3">📥 {t("admin.csvImport")}</h5>
                            <Form onSubmit={onCSVImport} id="CSVImportForm">
                                <Form.Group className="mb-3" controlId="CSVImportForm.CSVFile">
                                    <Form.Label>{t("admin.csvFileLabel")}</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="CSVFile"
                                        accept=".csv"
                                        required
                                        className="shadow-sm"
                                    />
                                    <Form.Text className="text-muted">{t("admin.csvFormatInfo")}</Form.Text>
                                </Form.Group>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={importing}
                                    className="shadow-sm"
                                >
                                    {importing ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            {t("admin.importButton")}...
                                        </>
                                    ) : (
                                        <>📤 {t("admin.importButton")}</>
                                    )}
                                </Button>
                            </Form>

                            {importResults.length > 0 && (
                                <Alert variant="info" className="mt-3 mb-0">
                                    <strong>{t("admin.importResults")}:</strong>
                                    <div style={{ maxHeight: "150px", overflowY: "auto" }} className="mt-2">
                                        {importResults.map((result, index) => (
                                            <div key={index} style={{ fontFamily: "monospace", fontSize: "0.85em" }}>
                                                {result}
                                            </div>
                                        ))}
                                    </div>
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </Collapse>
        </>
    );
}
